import React from 'react';
import { useNavigate } from 'react-router-dom';
import 'react-spring-bottom-sheet/dist/style.css';
import './sessionSummary.scss';
import { useSelector } from 'react-redux';
import { forEach, map } from 'lodash-es';
import { useImmer } from 'use-immer';
import { selectTableSessionId } from '../store/auth/selectors';
import { useGetOrdersQuery } from '../store/order/orderApi';
import { IGetOrderResponse } from '../proto/order/order';
import { OrderStatusSummary } from '../Menu/FloatingSummaryBubbles/OrderStatusSummary/OrderStatusSummary';
import { getOrderItemFinalPrice } from '../utils/getOrderItemPrice.util';
import { EOrderItemStatus, eOrderItemStatusToJSON } from '../proto/order/orderItemStatus.enum';

export const SessionSummary = (): React.JSX.Element => {
	const navigate = useNavigate();
	const tableSessionId = useSelector(selectTableSessionId);
	const [{ isOrderStatusSummarySheetOpen, selectedOrderIdForSummary }, setSelectedOrderState] = useImmer<{
		isOrderStatusSummarySheetOpen: boolean;
		selectedOrderIdForSummary: string;
	}>({ isOrderStatusSummarySheetOpen: false, selectedOrderIdForSummary: null });
	const { data: orders } = useGetOrdersQuery({
		tableSessionId,
		statuses: [EOrderItemStatus.PENDING, EOrderItemStatus.PREPARING, EOrderItemStatus.COMPLETED, EOrderItemStatus.CANCELLED],
	});

	const handleOrderMore = (): void => {
		navigate('/session/menu');
	};
	const handleOpenOrderSummarySheet = (orderId: string): void => {
		setSelectedOrderState((state) => {
			state.isOrderStatusSummarySheetOpen = true;
			state.selectedOrderIdForSummary = orderId;
		});
	};

	const handleOrderStatusDismiss = (): void => {
		setSelectedOrderState((state) => {
			state.isOrderStatusSummarySheetOpen = false;
		});
	};

	const getOrderTitle = (order: IGetOrderResponse): string => {
		return map(order.items, (item) => item.title).join(', ');
	};

	const getTotalOrderPrice = (order: IGetOrderResponse): string => {
		const total = order.items.reduce((sum, item) => sum + getOrderItemFinalPrice(item), 0);

		return `${total.toFixed(2)} ₪`;
	};

	const getOrderStatus = (order: IGetOrderResponse): string => {
		let hasPreparingItems = false;
		let isAllItemsPending = true;
		let isAllItemsCancelled = true;
		let isAllItemsCompletedOrCancelled = true;

		let orderStatus: EOrderItemStatus = EOrderItemStatus.PENDING;

		forEach(order.items, (item) => {
			if (item.status === EOrderItemStatus.PREPARING) {
				hasPreparingItems = true;
			}

			if (item.status !== EOrderItemStatus.PENDING) {
				isAllItemsPending = false;
			}

			if (item.status !== EOrderItemStatus.CANCELLED) {
				isAllItemsCancelled = false;

				if (item.status !== EOrderItemStatus.COMPLETED) {
					isAllItemsCompletedOrCancelled = false;
				}
			}
		});

		if (isAllItemsPending) {
			orderStatus = EOrderItemStatus.PENDING;
		} else if (isAllItemsCancelled) {
			orderStatus = EOrderItemStatus.CANCELLED;
		} else if (hasPreparingItems) {
			orderStatus = EOrderItemStatus.PREPARING;
		} else if (isAllItemsCompletedOrCancelled) {
			// If we got here it means all items are either completed or cancelled
			// and at least one is completed since the isAllItemsCancelled case is handled above
			orderStatus = EOrderItemStatus.COMPLETED;
		}

		switch (orderStatus) {
			case EOrderItemStatus.PENDING:
				return 'בהמתנה';
			case EOrderItemStatus.PREPARING:
				return 'בהכנה';
			case EOrderItemStatus.COMPLETED:
				return 'הושלם';
			case EOrderItemStatus.CANCELLED:
				return 'בוטל';
			default:
				return eOrderItemStatusToJSON(orderStatus);
		}
	};

	return (
		<div className="sessionSummaryContainer">
			<div className="sessionSummaryHeaderTitle">סיכום מצב שולחן</div>
			<div className="sessionSummaryBody">
				{map(orders, (order) => (
					<div key={order.id} className="sessionSummaryBodyItem" onClick={(): void => handleOpenOrderSummarySheet(order.id)}>
						<div className="sessionSummaryBodyItemData">
							<div className="sessionSummaryBodyItemName">{getOrderTitle(order)}</div>
							<div className="sessionSummaryBodyItemStatus">{getOrderStatus(order)}</div>
						</div>
						<div className="sessionSummaryBodyItemPrice">{getTotalOrderPrice(order)}</div>
					</div>
				))}
			</div>
			<div className="sessionSummaryActionButtons">
				<div className="sessionSummaryActionButton" onClick={handleOrderMore}>
					הזמן עוד
				</div>
				<div className="sessionSummaryActionButton">שלם חשבון</div>
			</div>
			<OrderStatusSummary onDismiss={handleOrderStatusDismiss} isOpen={isOrderStatusSummarySheetOpen} orderId={selectedOrderIdForSummary} />
		</div>
	);
};
